import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import {
  GRID_ACTION_TYPES,
  GridActionType,
  QueryKeyTypes,
} from "../../../assets/types";
import { isGridActionType, LocalStorageHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { LoadingIndicator } from "../../../assets/vfx/loadingIndicator/vfxLoadingIndicator";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { BannerDto } from "../../../services/dto";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { BannerPage } from "./bannerPage";
import { BannerService } from "./bannerService";
import { ViewProps } from "./props";
import { BannerView } from "./bannerView";
import { BannerForm } from "./bannerForm";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";
import { Dialog } from "../../../assets/vfx";

interface BannerContainerProps {}

export const BannerContainer: React.FC<BannerContainerProps> = (
  props: BannerContainerProps,
) => {
  const [communityId, setCommunityId] = React.useState<string | undefined>(
    () => LocalStorageHelper.getCommunityId() ?? undefined,
  );
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [data, setData] = React.useState<BannerDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN,
  );
  const bannerQuery = BannerService.getBannerQuery(data?.id ?? undefined);
  const lookupQuery = BannerService.getLookups(communityId);
  const { status: saveStatus, mutateAsync: saveBanner } = BannerService.saveBanner();
  const { status: deleteStatus, mutate: deleteBanner } =
    BannerService.deleteBanner(communityId);
  const [totalRecords, setTotalRecords] = React.useState<number>(0);
  const { setSearchAction, clearSearchAction } =
    useActionFromQuery(OnCustomEvent);
  const [showDialog, setShowDialog] = React.useState<boolean>(false);

  function EditRecord() {
    setData(bannerQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, bannerQuery.data?.id);
  }
  function OnCustomEvent(actionType: string, dataId: string) {
    if (isGridActionType(actionType)) {
      const drawerActionType: GridActionType =
        actionType === GRID_ACTION_TYPES.DOUBLE_CLICK
          ? GRID_ACTION_TYPES.EDIT
          : actionType;

      setDrawerType(drawerActionType);
      setData(
        actionType === GRID_ACTION_TYPES.NEW
          ? undefined
          : ({ id: dataId } as BannerDto),
      );
      setSearchAction(drawerActionType, dataId);
    }
    if (actionType == "delete") {
      setData({ id: dataId } as BannerDto);
      setShowDialog(true);
    }
  }

  const handleSubmit = async (values: BannerDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
    return await saveBanner(values, {
      onSuccess: (response: IActionResult<BannerDto>) =>
        handleResponse(response, "Banner saved successfully"),
    });
  };

  const closeDrawer = () => {
    setDrawerType(GRID_ACTION_TYPES.HIDDEN);
    clearSearchAction();
  };

  const handleDelete = () =>
    deleteBanner(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response: IActionResponse) =>
        handleResponse(response, "Banner deleted successfully"),
    });

  const renderDrawerContent = () => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <BannerForm
            show
            data={data}
            onHide={closeDrawer}
            onSubmit={handleSubmit}
            bannerTypes={lookupQuery.data?.bannerType}
            bannerTypesLoading={lookupQuery.isLoading}
            communityId={communityId}
            totalRecords={totalRecords}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return bannerQuery.isLoading ? (
          <LoadingIndicator isFullPage={true} />
        ) : (
          <BannerForm
            show={!!bannerQuery.data}
            data={bannerQuery.data}
            bannerTypes={lookupQuery.data?.bannerType}
            bannerTypesLoading={lookupQuery.isLoading}
            onHide={closeDrawer}
            onSubmit={handleSubmit}
            communityId={communityId}
            totalRecords={totalRecords}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return bannerQuery.isLoading ? (
          <LoadingIndicator isFullPage={true} />
        ) : (
          <BannerView
            data={bannerQuery.data}
            show={!!bannerQuery.data}
            onHide={closeDrawer}
            onEdit={EditRecord}
            onDelete={handleDelete}
          />
        );

      default:
        return null;
    }
  };
  function handleResponse(response: IActionResponse, snackbarMessage: string) {
    if (response) {
      if (response.isSuccess) {
        showSnackbar(snackbarMessage, MessageSeverity.Success);
        bannerQuery.refetch();
        queryClient.invalidateQueries({
          queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Grid]],
        });
        closeDrawer();
      } else {
        if (response.errorMessages) {
          const errorMsg: string | null = apiErrorMessage(
            response.errorMessages,
          );
          snackbarMessage = errorMsg ?? "Error";
        }
        showSnackbar(snackbarMessage, MessageSeverity.Error);
      }
    } else {
      showSnackbar(
        "Error, unable to process your request please try again later.",
        MessageSeverity.Error,
      );
    }
  }

  return (
    <>
      <BannerPage
        onCustomEvent={OnCustomEvent}
        setCommunityId={setCommunityId}
        onTotalRecordsChange={setTotalRecords}
      ></BannerPage>
      {renderDrawerContent()}
      {
        <Dialog
          contentText={"Are you sure you want to delete?"}
          contentTitle={"Delete"}
          onClose={() => setShowDialog(false)}
          onConfirm={() => {
            setShowDialog(false);
            handleDelete();
          }}
          open={showDialog}
          closeText={"No, Go Back"}
          confirmText={"Yes, Continue"}
        />
      }
    </>
  );
};
